import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

interface Blocker { type: string; count: number; message: string; }
interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'AUCTIONEER' | 'BIDDER' | 'SUPERADMIN';
  active: boolean;
  references: Record<string, number>;
  deletable: boolean;
  blockers: Blocker[];
  deactivatable: boolean;
  deactivationBlockers: Blocker[];
}

export default function UserAdminPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail?.error ?? err.response?.data?.detail ?? 'Failed to load users');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function describeError(err: any, fallback: string): string {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d?.blockers?.length) return `${d.error}: ${d.blockers.map((b: Blocker) => b.message).join('; ')}`;
    return d?.error ?? fallback;
  }

  async function remove(u: AdminUser) {
    if (!window.confirm(`Permanently delete user "${u.name}"? This cannot be undone.`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      await load();
    } catch (err: any) {
      setError(describeError(err, 'Delete failed'));
    } finally {
      setBusy(null);
    }
  }

  async function setActive(u: AdminUser, active: boolean) {
    const verb = active ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${active ? 'Reactivate' : 'Deactivate'} user "${u.name}"?`)) return;
    setBusy(u.id);
    try {
      await api.patch(`/users/${u.id}/active`, { active });
      await load();
    } catch (err: any) {
      setError(describeError(err, `Could not ${verb} user`));
    } finally {
      setBusy(null);
    }
  }

  function refSummary(u: AdminUser): string {
    const parts = u.blockers.map(b => b.message);
    return parts.length ? parts.join(', ') : '—';
  }

  return (
    <div style={styles.wrap}>
      <h3 style={{ marginTop: 0 }}>Users</h3>
      <p style={styles.hint}>
        New registrations arrive <strong>inactive</strong> and cannot be used until you activate
        them here — check the Role column first, since approving an account grants the role it
        asked for. Delete is available only for accounts with no references. Referenced accounts
        can be deactivated instead — they can no longer log in and are hidden from the bidder
        list, but their history is preserved.
      </p>
      {error && <p style={styles.error}>{error}</p>}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Active</th>
            <th style={styles.th}>References</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const isSelf = u.id === me?.id;
            return (
              <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                <td style={styles.td}>{u.name}{isSelf && <span style={styles.self}> (you)</span>}</td>
                <td style={styles.td}>{u.role}</td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>{u.active ? 'yes' : 'no'}</td>
                <td style={styles.td}>{refSummary(u)}</td>
                <td style={styles.td}>
                  <button
                    style={{ ...styles.btn, ...(u.deletable && !isSelf ? styles.danger : styles.disabled) }}
                    disabled={!u.deletable || isSelf || busy === u.id}
                    title={isSelf ? 'You cannot delete your own account'
                      : !u.deletable ? `Cannot delete: ${u.blockers.map(b => b.message).join(', ')}`
                      : 'Permanently delete'}
                    onClick={() => remove(u)}
                  >Delete</button>
                  {u.active ? (
                    <button
                      style={{ ...styles.btn, ...(!isSelf && u.deactivatable ? styles.warn : styles.disabled) }}
                      disabled={isSelf || !u.deactivatable || busy === u.id}
                      title={isSelf ? 'You cannot deactivate your own account'
                        : !u.deactivatable ? `Cannot deactivate: ${u.deactivationBlockers.map(b => b.message).join(', ')}`
                        : 'Deactivate'}
                      onClick={() => setActive(u, false)}
                    >Deactivate</button>
                  ) : (
                    <button
                      style={{ ...styles.btn, ...styles.ok }}
                      disabled={busy === u.id}
                      onClick={() => setActive(u, true)}
                    >Reactivate</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  hint: { fontSize: '0.85rem', color: '#666', margin: '0 0 1rem' },
  error: { color: '#c62828', fontSize: '0.9rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', borderBottom: '2px solid #eee', padding: '0.5rem', color: '#555' },
  td: { borderBottom: '1px solid #f0f0f0', padding: '0.5rem', verticalAlign: 'top' },
  self: { color: '#888', fontStyle: 'italic' },
  btn: { padding: '0.3rem 0.6rem', marginRight: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  danger: { background: '#c62828', color: 'white', borderColor: '#c62828' },
  warn: { background: '#ef6c00', color: 'white', borderColor: '#ef6c00' },
  ok: { background: '#2e7d32', color: 'white', borderColor: '#2e7d32' },
  disabled: { background: '#f5f5f5', color: '#aaa', cursor: 'not-allowed' },
};
